import LocationOnOutlined from "@mui/icons-material/LocationOnOutlined";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import {
  AddressAutocomplete,
  type AddressAutocompleteControllerOptions,
  type AddressSuggestion,
} from "@bw/shared/components";

export interface MaterialAddressAutocompleteProps
  extends AddressAutocompleteControllerOptions {
  placeholder?: string;
  error?: string;
}

function suggestionLabel(option: AddressSuggestion | string): string {
  return typeof option === "string" ? option : option.description;
}

/** Business-native MUI presentation over the shared Google Places controller. */
export function MaterialAddressAutocomplete({
  placeholder = "Search by name or address…",
  error,
  ...controllerOptions
}: MaterialAddressAutocompleteProps) {
  const label = controllerOptions.label ?? "Address";

  return (
    <AddressAutocomplete
      {...controllerOptions}
      render={(controller) => (
        <Autocomplete<AddressSuggestion, false, false, true>
          id={controller.inputId}
          freeSolo
          fullWidth
          autoHighlight
          filterOptions={(options) => options}
          getOptionLabel={suggestionLabel}
          inputValue={controller.value}
          loading={controller.suggestionsLoading}
          loadingText="Searching Google places…"
          noOptionsText="No matching places yet"
          open={
            controller.showSuggestionPanel &&
            controller.isLoaded &&
            !controller.isDisabled
          }
          options={controller.suggestions}
          disabled={controller.isDisabled}
          onFocus={controller.handleInputFocus}
          onBlur={controller.handleInputBlur}
          onInputChange={(_event, nextValue, reason) => {
            if (reason === "input" || reason === "clear") {
              controller.setValue(nextValue);
            }
          }}
          onChange={(_event, option) => {
            if (option && typeof option !== "string") {
              void controller.handleSuggestionSelect(option);
            }
          }}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <li key={key} {...optionProps}>
                <ListItemText
                  primary={option.main_text}
                  secondary={option.secondary_text || undefined}
                />
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              inputRef={controller.inputRef}
              label={label}
              placeholder={
                controller.isAvailable
                  ? controller.isLoaded
                    ? placeholder
                    : "Loading…"
                  : "Google address search unavailable"
              }
              error={Boolean(error)}
              helperText={
                error ??
                (controller.isAvailable
                  ? undefined
                  : "Google address search is unavailable until the Maps key is configured.")
              }
              slotProps={{
                inputLabel: params.slotProps.inputLabel,
                input: {
                  ...params.slotProps.input,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <LocationOnOutlined fontSize="small" />
                      </InputAdornment>
                      {params.slotProps.input.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {controller.suggestionsLoading ? (
                        <CircularProgress
                          aria-label="Searching places"
                          color="inherit"
                          size={18}
                        />
                      ) : null}
                      {params.slotProps.input.endAdornment}
                    </>
                  ),
                },
                htmlInput: params.slotProps.htmlInput,
              }}
            />
          )}
          slotProps={{
            listbox: { sx: { maxHeight: 320 } },
          }}
        />
      )}
    />
  );
}
